import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf")
});

const DecisionPDF = ({ data }) => {
  // Vérifications de sécurité
  if (!data || !data.employee) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.errorText}>Erreur: Données de l'employé manquantes</Text>
        </Page>
      </Document>
    );
  }
  
  // Helper function pour gérer le genre
  const getGenderInfo = (employee) => {
    const genre = employee.genre?.toLowerCase().trim();
    
    // Gérer différents cas possibles pour le genre féminin
    let isFemale = genre === 'femme' || genre === 'female' || genre === 'f' || genre === 'féminin';
    
    // Si le genre n'est pas spécifié, utiliser une heuristique basée sur le prénom
    if (!genre && employee.first_name) {
      const firstName = employee.first_name.toLowerCase().trim();
      // Liste de prénoms typiquement féminins (peut être étendue)
      const femaleNames = ['cherifa', 'fatima', 'khadidja', 'amina', 'samira', 'nadia', 'sarah', 'leila', 'zohra', 'malika'];
      isFemale = femaleNames.some(name => firstName.includes(name));
    }
    
    return {
      title: isFemale ? 'Madame' : 'Monsieur',
      titleLower: isFemale ? 'madame' : 'monsieur',
    };
  };
  
  // Formatter le numéro de référence (gère les anciens et nouveaux formats)
  const formatReference = (ref) => {
    if (!ref) return '0001DG/2MPI/2026';
    
    // Si la référence est déjà au nouveau format (4 chiffres + DG/2MPI + année)
    if (ref.match(/^\d{4}DG\/2MPI\/\d{4}$/)) {
      return ref;
    }
    
    // Si c'est une ancienne référence (ATT-, CERT-, DEC-), la retourner telle quelle
    if (ref.match(/^(ATT|CERT|DEC)-\d+$/)) {
      return ref;
    }
    
    // Sinon, retourner la référence par défaut
    return ref;
  };

  const BRAND = getBrandingForEmployee(data.employee);
  const genderInfo = getGenderInfo(data.employee);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.logo}>
            <Image src={BRAND.pdfLogo} style={styles.logoImage} />
          </View>

          <View style={styles.headerRow}>
            <Text style={styles.headerReference}>
              Réf : {formatReference(data.reference)}
            </Text>
            <Text style={styles.headerDate}>
              Boufarik Le, {formatDate(data.created_at)}
            </Text>
          </View>

          <Text style={styles.mainTitle}>
            DÉCISION          </Text>

          <View style={styles.formGroup}>
            <Text style={styles.fieldArticle}>
              Le Président Du Conseil D'administration,
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              - Vu la loi 90/11 du 21 Avril 1990 relative aux relations de travail modifiée et complétée
            </Text>

            <Text style={styles.fieldArticle}>
              - Vu le statut de création de la Spa 2MP INDUSTRY N°1426/2019 du : 23/12/2019
            </Text>

            <Text style={styles.fieldArticle}>
              - Vu le statut modifié de la Spa 2MP INDUSTRY N° 537/2022 du : 31/08/2022
            </Text>

            <Text style={styles.fieldArticle}>
              - Vu le statut modifié de la Spa 2MP INDUSTRY N° 481/2023 du : 04/07/2023
            </Text>

            <Text style={styles.fieldArticle}>
              - Vu le statut modifié de la Spa 2MP INDUSTRY N° 430/2024 du : 29,30/12/2024
            </Text>

            <Text style={styles.fieldArticle}>
              - Vu le Procès-verbal du conseil d'administration, 2MP INDUSTRY SPA, tenu en date du 18 décembre 2019, portant désignation de Monsieur MOUSSAOUI Bilel, en qualité de Président du Conseil d'Administration.
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              Vu le contrat de travail N° <Text style={styles.fieldValue}>{data.contract_number || '[N° contrat]'}</Text> , du : <Text style={styles.fieldValue}>{formatDate(data.contract_date) || '[DATE DU CONTRAT]'}</Text> à temps plein, liant {genderInfo.title} <Text style={styles.fieldValue}>{data.employee.first_name || '[NOM]'} {data.employee.last_name || '[PRENOM]'}</Text> a l'entreprise en sa qualité de <Text style={styles.fieldValue}>{data.employee?.poste || '[POSTE]'}</Text>.
            </Text>

            <Text style={styles.fieldArticle}>
              En vertu des pouvoirs qui me sont conférés, et après avis de la hiérarchie
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 30, fontWeight: 'bold', textAlign: 'center' }]}>
              DÉCIDE
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 , marginBottom:35 }]}>
              Article 1 : {data.decision_articles && data.decision_articles[0] ? data.decision_articles[0].content : '..................................................'}
            </Text>

            <Text style={styles.fieldArticle}>
              Article 2 : Cette décision prend effet immédiatement et sera notifiée aux services concernés pour exécution.
            </Text>

            <Text style={styles.fieldArticle}>
              Article 3 : <Text style={styles.fieldValue}>{data.charge_execution || '..........................'}</Text> est chargé(e) de l'exécution de la présente décision.
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              La présente décision prendra effet le : <Text style={styles.fieldValue}>{formatDate(data.decision_start_date) || '[Date debut decision]'}</Text>
            </Text>
          </View>

          <View style={styles.signature}>
            <View style={styles.signatureRow}>
              <Text style={styles.signatureName}>MOUSSAOUI Bilel</Text>
              <Text style={styles.signatureTitle}>Président du Conseil d'Administration</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 12,
    padding: 40,
    paddingTop: 60,
    backgroundColor: '#FFFFFF'
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  logo: {
    width: '100%',
    marginBottom: 10,
  },
  logoImage: {
    width: '100%',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerReference: {
    fontSize: 11,
    textAlign: 'left',
  },
  headerDate: {
    fontSize: 11,
    textAlign: 'right',
  },
  mainTitleArabic: {
    fontFamily: 'Amiri',
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
    color: '#1a237e'
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 30,
    color: '#1a237e'
  },
  formGroup: {
    flex: 1,
    marginBottom: 20
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    borderBottom: '1pt solid #eee',
    paddingBottom: 8
  },
  field: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold'
  },
  fieldValue: {
    fontWeight: 'normal',
    color: '#333'
  },
  arabicLabel: {
    fontFamily: 'Amiri',
    fontSize: 14,
    color: '#666',
    marginLeft: 20
  },
  fieldArticle: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 15,
    textAlign: 'justify'
  },
  signature: {
    marginTop: 60,
    alignItems: 'flex-end'
  },
  signatureRow: {
    alignItems: 'flex-end',
    textAlign: 'right'
  },
  signatureTitle: {
    fontSize: 12,
    textAlign: 'right',
    marginTop: 5
  },
  signatureName: {
    fontSize: 12,
    textAlign: 'right',
    fontWeight: 'bold'
  },
  errorText: {
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
    marginTop: 100
  }
});

export default DecisionPDF;
